import WebSocketMessage, {
  WebSocketConnectionState,
  WebSocketMessageType,
} from "../../model/WebSocketMessage";
import { currentUserId } from "../auth";
import eventEmitter from "../event";
import { handleCancelNotification } from "./cancelNotificationHandler";
import { handleCurrentlyViewing } from "./currentlyViewingHandler";
import { handleNotification } from "./notificationHandler";
import { handlePing } from "./pingHandler";
import { handleTinyNotification } from "./tinyNotificationHandler";
import { handleTracking } from "./trackingHandler";

export class WebSocketGlobalState {
  private static connection?: boolean;
  private static ws?: WebSocket;
  private static lastPingTime: number = Date.now();
  public static getWebSocket = () => WebSocketGlobalState.ws;
  public static anonymousIdentity = true;
  public static forceAnonymousIdentity = false;
  public static disconnectedTimes = 0;
  public static connectionLimbo = false;
  public static pingInterval?: NodeJS.Timeout;

  static setWebSocket = (ws: WebSocket | undefined) => {
    WebSocketGlobalState.ws = ws;
  };
  static setPing() {
    WebSocketGlobalState.lastPingTime = Date.now();
  }
  static getPing() {
    return WebSocketGlobalState.lastPingTime;
  }
}

const messageTypesToReceive = [
  WebSocketMessageType.PUSH_NOTIFICATION,
  WebSocketMessageType.TINY_NOTIFICATION,
  WebSocketMessageType.IDENTITY,
  WebSocketMessageType.IDENTITY_ERROR,
  WebSocketMessageType.TRACKING,
  WebSocketMessageType.KILL,
  WebSocketMessageType.WELCOME,
  WebSocketMessageType.PUSH_NOTIFICATION_CANCEL,
  WebSocketMessageType.CURRENTLY_VIEWING,
  WebSocketMessageType.PING,
];

const handlers: {
  [key: string]: (message: WebSocketMessage) => Promise<void>;
} = {
  PUSH_NOTIFICATION: handleNotification,
  TINY_NOTIFICATION: handleTinyNotification,
  PUSH_NOTIFICATION_CANCEL: handleCancelNotification,
  TRACKING: handleTracking,
  CURRENTLY_VIEWING: handleCurrentlyViewing,
  PING: handlePing,
};

const handleCloseWebSocket = () => {
  WebSocketGlobalState.disconnectedTimes++;
  WebSocketGlobalState.connectionLimbo = true;
  WebSocketGlobalState.setWebSocket(undefined);
  if (WebSocketGlobalState.pingInterval) {
    clearInterval(WebSocketGlobalState.pingInterval);
    WebSocketGlobalState.pingInterval = undefined;
  }
  setTimeout(() => {
    eventEmitter.emit(
      "websocket:connectionState",
      WebSocketConnectionState.CONNECTING,
    );
  }, 500 * WebSocketGlobalState.disconnectedTimes);
  // if we are in limbo, we need to wait for the timeout to finish
  setTimeout(() => {
    // try to create another websocket connection
    WebSocketGlobalState.connectionLimbo = false;
    createWebSocket();
  }, 1000 * WebSocketGlobalState.disconnectedTimes);
};

const createWebSocket = () => {
  const ws = new WebSocket(
    process.env.REACT_APP_WEBSOCKET_URL ?? `wss://${window.location.host}/wss`,
  );

  ws.onopen = () => {
    // identify the user on the server to begin with
    ws?.send(
      JSON.stringify(createWebSocketMessage(WebSocketMessageType.IDENTITY, {})),
    );
    if (ws) setupWebSocket(ws);
    eventEmitter.emit(
      "websocket:connectionState",
      WebSocketConnectionState.CONNECTED,
    );
  };
  ws.onerror = (event) => {
    console.error("WebSocket error observed:", event);
    eventEmitter.emit(
      "websocket:connectionState",
      WebSocketConnectionState.ERROR,
    );
  };
  ws.onclose = (event) => {
    if (event.wasClean) {
      eventEmitter.emit(
        "websocket:connectionState",
        WebSocketConnectionState.DISCONNECTED,
      );
    } else {
      eventEmitter.emit(
        "websocket:connectionState",
        WebSocketConnectionState.ERROR,
      );
    }
    handleCloseWebSocket();
  };

  if (!WebSocketGlobalState.pingInterval) {
    WebSocketGlobalState.pingInterval = setInterval(() => {
      const webSocketMessageToSend = createWebSocketMessage(
        WebSocketMessageType.PING,
        {},
      );

      WebSocketGlobalState.getWebSocket()?.send(
        JSON.stringify(webSocketMessageToSend),
      );
    }, 2000);
  }

  setupWebSocket(ws);
};

export const getWebSocket = () => {
  if (WebSocketGlobalState.connectionLimbo) return null;
  if (!WebSocketGlobalState.getWebSocket()) {
    createWebSocket();
  }
  return WebSocketGlobalState.getWebSocket();
};

/**
 * Sets up the websocket connection and adds it to the static class
 */
export const setupWebSocket = (ws: WebSocket) => {
  WebSocketGlobalState.setWebSocket(ws);
  ws.onmessage = (event) => {
    // parse to WebSocketMessage
    try {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    } catch (e) {
      console.error("Error parsing websocket message", e);
    }
  };
};

/**
 * Validate a websocket message that is received from the server
 * - If no message type is set, the message is invalid
 * - If the message type is not one we can receive, then the message is invalid
 */
export const validateWebSocketMessage = (message: WebSocketMessage) => {
  if (!message.type) {
    return false;
  }
  if (!messageTypesToReceive.includes(message.type)) {
    return false;
  }
  return true;
};

/**
 * Creates a WebSocketMessage object that can be sent to the server
 */
export const createWebSocketMessage = (
  type: WebSocketMessageType,
  data: object,
) => {
  let userIdToUse;
  try {
    if (WebSocketGlobalState.forceAnonymousIdentity) {
      throw new Error("Anonymous identity");
    }
    userIdToUse = currentUserId();

    // check if we are now logged in somehow
    // if we are, we need to send the identity message again
    const oldAnonymous = WebSocketGlobalState.anonymousIdentity;
    WebSocketGlobalState.anonymousIdentity = false;
    if (oldAnonymous != WebSocketGlobalState.anonymousIdentity) {
      WebSocketGlobalState.getWebSocket()?.send(
        JSON.stringify(
          createWebSocketMessage(WebSocketMessageType.IDENTITY, {}),
        ),
      );
    }
  } catch (e) {
    // if there is a failed attempt to get the user_id, we are anonymous
    userIdToUse = "anonymous";
    // if we are now anonymous, we need to send the identity message again eventually
    WebSocketGlobalState.anonymousIdentity = true;
  }
  const message: WebSocketMessage = {
    type,
    data,
    user_id: userIdToUse,
  };
  return message;
};

/**
 * Handles a websocket message that is received from the server
 * Gets the appropriate handler for the message type and calls it
 */
export const handleWebSocketMessage = (message: WebSocketMessage) => {
  // check for validation of the message
  if (!validateWebSocketMessage(message) || !message.type) return;
  const handler = handlers[message.type?.toString()];
  if (!handler) {
    return;
  }
  // invoke the handler
  handler(message);
};
